import { Signal } from '@angular/core';
import { isDebugEnabled } from 'shared/util-runtime-config';
import { FabricImage, FabricText, Rect, Textbox, util } from 'fabric';
import { IDisplaySlide, SlideType } from '@pcp/core/interfaces';
import { ITransferDynamicContentSlide } from '@pcp/streaming';
import { BandedObject, PointerContext, SlideCapability, SlideHostContext } from '../slide-capability';
import { TextboardPagingIcons } from '../textboard-icon-config';
import {
    assignPages,
    clampToTwoLines,
    computeTextboardArea,
    fabricLineHeight,
    HEADLINE_FONT_SIZE,
    HEADLINE_LINE_HEIGHT,
    HEADLINE_TEXT_X,
    HEADLINE_WRAP_WIDTH,
    headlineTop,
    letterSpacingToCharSpacing,
    pageCountOf,
    paragraphTop,
    PAGING_BUTTON_SIZE,
    PAGING_NEXT_POSITION,
    PAGING_PREV_POSITION,
    stripLineBreakMarkers,
    TEXTBOARD_BULLET,
    TEXTBOARD_BULLET_X,
    TEXTBOARD_CONTENT_TOP,
    TEXTBOARD_FONT_SIZE,
    TEXTBOARD_LINE_HEIGHT,
    TEXTBOARD_TEXT_X,
    type TextboardArea,
    type TextboardAreaOffsets,
} from './textboard-layout';

/** Concrete text styles bridged from the theme CSS custom properties (design D5). */
export interface TextboardTextStyles {
    color: string;
    lightColor: string;
    /** Bullet marker fill; `null` = theme leaves it `inherit` (TUT) → use the paragraph text color. */
    bulletColor: string | null;
    fontFamily: string;
    headlineColor: string;
    headlineFontFamily: string;
    /**
     * Effective CSS `letter-spacing` (px) inherited by the board's DOM host. The SVG textboard's
     * `<text>` inherits the app's `body { letter-spacing }`, so its wrap depends on it; the canvas
     * must replicate it via fabric `charSpacing` or it wraps one word too late (see
     * {@link letterSpacingToCharSpacing}). `0` when the theme leaves it `normal`.
     */
    letterSpacingPx: number;
}

/** Everything the host supplies (Decision C13 — the capability never injects Angular itself). */
export interface TextboardContentDeps {
    /** Synced per-layer dynamic-content list (append order; reset = empty). */
    content: Signal<ITransferDynamicContentSlide[]>;
    /** Synced current page (1-based; `undefined` until hydrated). */
    currentPage: Signal<number | undefined>;
    /** Intent UP: show this page (button click / append jump). Followers never call it. */
    onPageIntent: (page: number) => void;
    styles: TextboardTextStyles;
    icons: TextboardPagingIcons;
    /** Brand-configurable right/bottom edge offsets of the text area (PCP vs TUT differ). */
    layout: TextboardAreaOffsets;
}

type Ephemeral<T> = T & { _yjsEphemeral?: boolean };

/** One appended `text` item projected to canvas objects, plus its page placement. */
interface ParagraphObjects {
    /** Reuse key: preprocessed caption + fill (light-text slides recreate on flag change). */
    key: string;
    bullet: Ephemeral<FabricText>;
    text: Ephemeral<Textbox>;
    page: number;
}

type PagingSide = 'prev' | 'next';

/**
 * TextboardContent — renders textboard (`X`) / textboard-generated (`TB`) dynamic text content as a
 * {@link SlideCapability} (`canvas-textboard-slides`): appended `text` items become bullet + wrapped
 * `fabric.Textbox` paragraphs in the **content** z-band, paginated at 7 lines/page with
 * whole-paragraph page moves (design D3); plus the headline (slide caption in the header band,
 * design D4) and the in-canvas prev/next paging buttons (overlay band, pointer claim-chain).
 *
 * Layout is a PURE FUNCTION of the synced item list, computed in logical 1280×720 units with
 * identical font inputs on every client — page breaks are deterministic across teacher/beamer/
 * students and are never synced themselves. Every reconcile is a full relayout pass (cheap string
 * measurement; the shared `DynamicContentSource` per-item async machinery is wrong-shaped for text,
 * where a paragraph's page depends on all prior paragraphs — design D3).
 *
 * Page state: the capability DISPLAYS the synced page (clamped to the computed page count — the
 * #22502 "ignore beyond last page" guard) and never re-emits a received page change (no echo).
 * It emits a page INTENT only for a local button activation and for the writer's append-jump
 * (jump to the appended paragraph's page — SVG `appendText` parity).
 *
 * Text objects are `_yjsEphemeral` (never synced as drawings) but exportable (text is content);
 * paging buttons are `_yjsEphemeral` + `excludeFromExport` (controls, like the bookpage folds).
 */
export class TextboardContent implements SlideCapability {
    /** Body text area (wrap width + page capacity) resolved from the brand offsets — constant per app. */
    private readonly area: TextboardArea;
    private paragraphs: ParagraphObjects[] = [];
    private pageCount = 1;
    /** Cache key of the last relayout (captions + fill); relayout is skipped while it matches. */
    private lastLayoutKey: string | null = null;
    /** Item count seen by the previous reconcile; `null` until the first pass (no append-jump on late-join replay). */
    private lastItemCount: number | null = null;
    /**
     * Bumped once the textboard webfonts finish loading. Gates paragraph reuse (see {@link relayout})
     * and the headline rebuild, so a bump recreates every Textbox — the only way to make fabric
     * re-measure with the real (rather than fallback) font metrics after a late font load.
     */
    private fontGeneration = 0;
    /** Generation the current {@link paragraphs} were built under; a mismatch drops reuse (re-measure). */
    private relayoutGeneration = 0;

    private headline?: Ephemeral<Textbox>;
    private headlineKey: string | null = null;

    // Per-slide flags (from slide data, not synced state).
    private hidePageButton = false;
    private lightText = false;
    private caption = '';
    private showCaption = false;

    private prevButton?: Ephemeral<FabricImage>;
    private nextButton?: Ephemeral<FabricImage>;
    private debugRect?: Ephemeral<Rect>;
    private hovered: PagingSide | null = null;
    /** Preloaded icon elements by URL; buttons contribute only once their icons are loaded. */
    private readonly iconElements = new Map<string, HTMLImageElement>();
    private disposed = false;

    constructor(
        private readonly ctx: SlideHostContext,
        private readonly deps: TextboardContentDeps,
    ) {
        this.area = computeTextboardArea(this.deps.layout);
        void this.preloadIcons();
        void this.loadFonts();
    }

    /**
     * Load the textboard body + headline webfonts, then force a re-measure.
     *
     * Canvas text measurement uses only the `@font-face` fonts ALREADY loaded — and, unlike CSS/SVG,
     * a canvas render never triggers an on-demand font load. A variable webfont referenced only here
     * (e.g. PCP's "Jost Variable", bundled but loaded lazily) would therefore stay unloaded, so
     * fabric's `measureText` falls back to a different family and computes the WRONG wrap points.
     * `fonts.ready` alone does not help: nothing made the face pending, so it resolves without ever
     * loading it. We must explicitly {@link FontFaceSet.load} the exact descriptors we render with.
     *
     * After the load we bump {@link fontGeneration} (which gates paragraph reuse) and clear the
     * layout caches (`lastLayoutKey` re-enters {@link relayout}; `headlineKey` rebuilds the headline),
     * so the next reconcile RECREATES every paragraph/headline and re-measures with the real metrics —
     * every client of a brand converges on the same loaded-font layout, keeping page breaks deterministic.
     */
    private async loadFonts(): Promise<void> {
        const fonts = document.fonts;
        if (!fonts?.load) return;
        const descriptors = [
            `normal ${TEXTBOARD_FONT_SIZE}px ${this.deps.styles.fontFamily}`,
            `bold ${HEADLINE_FONT_SIZE}px ${this.deps.styles.headlineFontFamily}`,
        ];
        try {
            // allSettled: a malformed descriptor / missing face must not block re-measuring the rest.
            await Promise.allSettled(descriptors.map(descriptor => fonts.load(descriptor)));
        } catch {
            // A synchronous throw (e.g. jsdom's unimplemented FontFaceSet) → keep fallback metrics.
        }
        if (this.disposed) return;
        this.fontGeneration++;
        this.lastLayoutKey = null;
        this.headlineKey = null;
        this.ctx.canvas.requestRenderAll();
    }

    /**
     * New slide on this layer: pick up the slide-data flags; content follows via the signal. The host
     * REUSES this capability instance across slide changes (it reconciles capabilities by kind, only
     * disposing dropped ones — `SlideCanvasRendererComponent.onSlideChange`), so the reconcile state
     * must be reset too: otherwise the previous slide's {@link lastItemCount} makes the first relayout
     * of a larger board look like an append and fires a spurious writer page-jump (moving followers,
     * overriding the new slide's synced page), and a stale {@link lastLayoutKey} could skip relayout
     * entirely if the new content happens to hash identically.
     */
    onSlideChange(slide: IDisplaySlide): void {
        this.hidePageButton = slide.hidePageButton === true;
        this.lightText = slide.lightTextColor === true;
        this.caption = slide.caption ?? '';
        this.showCaption = slide.showCaption === true;
        // Reused instance: a slide switch is not an append, and must relayout the new content.
        this.lastItemCount = null;
        this.lastLayoutKey = null;
        this.setHover(null);
    }

    contributeObjects(): BandedObject[] {
        this.relayout();
        this.reconcileHeadline();
        const out: BandedObject[] = [];
        if (this.headline) out.push({ object: this.headline, band: 'content' });
        const page = this.displayedPage();
        for (const p of this.paragraphs) {
            if (p.page !== page) continue;
            out.push({ object: p.bullet, band: 'content' }, { object: p.text, band: 'content' });
        }
        out.push(...this.contributeButtons());
        // Read live (not a recipe-time snapshot) so a debug toggle adds/removes the rect on the
        // very next band re-assert — same live behavior as the hotspot overlay.
        if (isDebugEnabled()) out.push({ object: this.ensureDebugRect(), band: 'overlay' });
        return out;
    }

    /**
     * Dev-only magenta dashed rectangle marking the usable text area — parity with develop's
     * `SvgTextBoard.renderDebugBoundsIfEnabled()`. Spans the wrap area: x from the paragraph text
     * start, width = wrap width, y/height = the content band top + available height.
     */
    private ensureDebugRect(): Ephemeral<Rect> {
        this.debugRect ??= (() => {
            const rect = new Rect({
                left: TEXTBOARD_TEXT_X,
                top: TEXTBOARD_CONTENT_TOP,
                width: this.area.wrapWidth,
                height: this.area.availableHeight,
                fill: 'rgba(255, 0, 255, 0.1)',
                stroke: 'magenta',
                strokeWidth: 2,
                strokeDashArray: [5, 5],
                selectable: false,
                evented: false,
                objectCaching: false,
                excludeFromExport: true,
            }) as Ephemeral<Rect>;
            rect._yjsEphemeral = true;
            return rect;
        })();
        return this.debugRect;
    }

    // ── Pointer claim-chain (Decision C12) — the paging buttons are the only interactive part ──

    /** Paging input is live only for a WRITER with element usage enabled, in a non-freehand tool mode. */
    private isInteractive(): boolean {
        const tool = this.ctx.interaction.activeTool();
        return (
            this.ctx.interaction.isWriter() &&
            !this.ctx.interaction.disableElementUsage() &&
            (tool === null || tool === 'selection')
        );
    }

    /**
     * Claim the pointer over a RENDERED button. Disabled buttons still swallow the click (SVG parity),
     * but a button whose icon has not loaded (still fetching, or a failed/404 icon) is not painted —
     * so it must NOT claim, or its footprint becomes an invisible click-eater / phantom pager. Gate on
     * the button instance existing ({@link buttonFor}), which is created only once its icon is loaded.
     */
    claims(pointer: PointerContext): boolean {
        if (!this.isInteractive() || !this.buttonsVisible()) return false;
        const side = this.hitTestSide(pointer.viewportPoint);
        return side !== null && this.buttonFor(side) !== undefined;
    }

    onPointerDown(pointer: PointerContext): void {
        const side = this.hitTestSide(pointer.viewportPoint);
        if (!side || !this.sideEnabled(side)) return;
        const page = this.displayedPage() + (side === 'next' ? 1 : -1);
        // Guard (#22502 heritage): never request a page outside [1, pageCount].
        if (page < 1 || page > this.pageCount) return;
        this.deps.onPageIntent(page);
    }

    onPointerMove(pointer: PointerContext): void {
        const hit = this.isInteractive() && this.buttonsVisible() ? this.hitTestSide(pointer.viewportPoint) : null;
        // Only a rendered button hovers (same guard as claims — no hover feedback over an unloaded icon).
        const side = hit && this.buttonFor(hit) ? hit : null;
        this.setHover(side && this.sideEnabled(side) ? side : null);
    }

    dispose(): void {
        this.disposed = true;
        for (const p of this.paragraphs) {
            this.ctx.canvas.remove(p.bullet);
            this.ctx.canvas.remove(p.text);
        }
        this.paragraphs = [];
        if (this.headline) this.ctx.canvas.remove(this.headline);
        this.headline = undefined;
        if (this.prevButton) this.ctx.canvas.remove(this.prevButton);
        if (this.nextButton) this.ctx.canvas.remove(this.nextButton);
        this.prevButton = this.nextButton = undefined;
        if (this.debugRect) this.ctx.canvas.remove(this.debugRect);
        this.debugRect = undefined;
        this.iconElements.clear();
    }

    // ── Paragraph layout (design D3) ─────────────────────────────────────────────────────

    /** The page to show: the synced page clamped to what the current content supports. */
    private displayedPage(): number {
        return Math.min(Math.max(this.deps.currentPage() ?? 1, 1), this.pageCount);
    }

    /**
     * Full relayout pass — a pure projection of the synced item list into positioned paragraph
     * objects. Skipped (cheap key check) while the content and fill are unchanged, so calling it
     * from every render pass is safe. Emits the writer's append-jump page intent on item growth.
     */
    private relayout(): void {
        const items = this.deps.content();
        const texts = items
            .filter(item => item.type === SlideType.text)
            .map(item => stripLineBreakMarkers(item.caption ?? ''));
        const fill = this.lightText ? this.deps.styles.lightColor : this.deps.styles.color;
        const layoutKey = `${fill}\u0000${texts.join('\u0000')}`;

        if (layoutKey !== this.lastLayoutKey) {
            this.lastLayoutKey = layoutKey;
            // Reuse is sourced from the prior paragraphs ONLY within the same font generation: an
            // unchanged caption+fill yields an unchanged key and would reuse a Textbox, keeping its
            // stale (fallback-font) wrap. A generation bump (a webfont just loaded) empties the reuse
            // source, so every paragraph is recreated and re-measured with the real metrics. `outgoing`
            // is the true prior list — always removed proactively below, whether or not reuse applied.
            const outgoing = this.paragraphs;
            const previous = this.relayoutGeneration === this.fontGeneration ? outgoing : [];
            this.relayoutGeneration = this.fontGeneration;
            this.paragraphs = texts.map((text, i) => {
                const key = `${fill}\u0000${text}`;
                const existing = previous[i];
                if (existing?.key === key) return existing;
                return this.createParagraph(text, key, fill);
            });
            // Objects no longer wanted are dropped by the host's band re-assertion; remove
            // proactively so a same-index replacement doesn't leave both on the canvas.
            for (const old of outgoing) {
                if (!this.paragraphs.includes(old)) {
                    this.ctx.canvas.remove(old.bullet);
                    this.ctx.canvas.remove(old.text);
                }
            }

            const placements = assignPages(
                this.paragraphs.map(p => p.text.textLines.length),
                this.area.pageLineCapacity,
            );
            placements.forEach((placement, i) => {
                const p = this.paragraphs[i];
                p.page = placement.page;
                const top = paragraphTop(placement.startLine);
                p.text.set({ top });
                p.bullet.set({ top });
            });
            this.pageCount = pageCountOf(placements);
        }

        // Append-jump (SVG `appendText` parity): the WRITER moves to the appended paragraph's page
        // (followers receive the synced page instead). `lastItemCount === null` = first reconcile
        // (late-join / refresh replay) — restore, don't jump.
        const grew = this.lastItemCount !== null && texts.length > this.lastItemCount;
        this.lastItemCount = texts.length;
        if (grew && this.ctx.interaction.isWriter() && this.paragraphs.length > 0) {
            const target = this.paragraphs[this.paragraphs.length - 1].page;
            if (target !== (this.deps.currentPage() ?? 1)) this.deps.onPageIntent(target);
        }
    }

    private createParagraph(text: string, key: string, fill: string): ParagraphObjects {
        // Body text: 38px, normal weight, on the fixed 40px line grid (develop redesign).
        // `charSpacing` replicates the app's inherited CSS letter-spacing so the canvas wraps like
        // the SVG textboard (whose `<text>` inherits it); without it, text measures narrower and
        // breaks a word too late.
        const shared = {
            top: 0,
            fontSize: TEXTBOARD_FONT_SIZE,
            lineHeight: fabricLineHeight(TEXTBOARD_LINE_HEIGHT, TEXTBOARD_FONT_SIZE),
            fontWeight: 'normal',
            fontFamily: this.deps.styles.fontFamily,
            charSpacing: letterSpacingToCharSpacing(this.deps.styles.letterSpacingPx, TEXTBOARD_FONT_SIZE),
            fill,
            selectable: false,
            evented: false,
            objectCaching: false,
            excludeFromExport: false,
        } as const;
        const textbox = new Textbox(text, {
            ...shared,
            left: TEXTBOARD_TEXT_X,
            width: this.area.wrapWidth,
            editable: false,
        }) as Ephemeral<Textbox>;
        textbox._yjsEphemeral = true;
        const bullet = new FabricText(TEXTBOARD_BULLET, {
            ...shared,
            left: TEXTBOARD_BULLET_X,
        }) as Ephemeral<FabricText>;
        // Bullet color is themeable independent of the text (PCP: yellow #fac426; TUT: inherits
        // the text color). `null` → the theme left it `inherit`, so fall back to the text fill.
        bullet.set({ fill: this.deps.styles.bulletColor ?? fill });
        bullet._yjsEphemeral = true;
        return { key, bullet, text: textbox, page: 1 };
    }

    // ── Headline (design D4) ─────────────────────────────────────────────────────────────

    private reconcileHeadline(): void {
        const wanted = this.showCaption && this.caption.length > 0 ? this.caption : null;
        const key = wanted === null ? null : `${this.deps.styles.headlineColor}\u0000${wanted}`;
        if (key === this.headlineKey) return;
        this.headlineKey = key;
        if (this.headline) {
            this.ctx.canvas.remove(this.headline);
            this.headline = undefined;
        }
        if (wanted === null) return;

        // Headline keeps the original 45px bold band styling (only body text shrank to 38).
        const box = new Textbox(wanted, {
            left: HEADLINE_TEXT_X,
            top: 0,
            width: HEADLINE_WRAP_WIDTH,
            fontSize: HEADLINE_FONT_SIZE,
            lineHeight: fabricLineHeight(HEADLINE_LINE_HEIGHT, HEADLINE_FONT_SIZE),
            fontWeight: 'bold',
            fontFamily: this.deps.styles.headlineFontFamily,
            charSpacing: letterSpacingToCharSpacing(this.deps.styles.letterSpacingPx, HEADLINE_FONT_SIZE),
            fill: this.deps.styles.headlineColor,
            selectable: false,
            evented: false,
            editable: false,
            objectCaching: false,
            excludeFromExport: false,
        }) as Ephemeral<Textbox>;
        const clamped = clampToTwoLines(box.textLines);
        if (clamped.text !== wanted) box.set({ text: clamped.text });
        box.set({ top: headlineTop(clamped.lineCount) });
        box._yjsEphemeral = true;
        this.headline = box;
    }

    // ── Paging buttons (spec: visible once >1 page exists or current page > 1) ───────────

    private buttonsVisible(): boolean {
        if (this.hidePageButton) return false;
        return this.hasPrevPage() || this.hasNextPage();
    }

    private hasPrevPage(): boolean {
        return this.displayedPage() > 1;
    }

    private hasNextPage(): boolean {
        return this.pageCount > 1 && this.displayedPage() < this.pageCount;
    }

    private sideEnabled(side: PagingSide): boolean {
        return side === 'prev' ? this.hasPrevPage() : this.hasNextPage();
    }

    private contributeButtons(): BandedObject[] {
        if (!this.buttonsVisible()) return [];
        const out: BandedObject[] = [];
        for (const side of ['prev', 'next'] as const) {
            const button = this.ensureButton(side);
            if (!button) continue; // icons still loading
            this.syncButtonIcon(button, side);
            out.push({ object: button, band: 'overlay' });
        }
        return out;
    }

    /** The cached button for a side, or `undefined` while its icon has not loaded (never rendered). */
    private buttonFor(side: PagingSide): Ephemeral<FabricImage> | undefined {
        return side === 'prev' ? this.prevButton : this.nextButton;
    }

    private ensureButton(side: PagingSide): Ephemeral<FabricImage> | undefined {
        const existing = this.buttonFor(side);
        if (existing) return existing;
        const element = this.iconElements.get(this.deps.icons[side].enabled);
        if (!element) return undefined;
        const position = side === 'prev' ? PAGING_PREV_POSITION : PAGING_NEXT_POSITION;
        const button = new FabricImage(element, {
            left: position.x,
            top: position.y,
            originX: 'left',
            originY: 'top',
            selectable: false,
            evented: false,
            hoverCursor: 'pointer',
            objectCaching: false,
            excludeFromExport: true,
        }) as Ephemeral<FabricImage>;
        if (button.width) button.scaleX = PAGING_BUTTON_SIZE / button.width;
        if (button.height) button.scaleY = PAGING_BUTTON_SIZE / button.height;
        button._yjsEphemeral = true;
        if (side === 'prev') this.prevButton = button;
        else this.nextButton = button;
        return button;
    }

    /** Swap the button's element to match its state (enabled / disabled / hover). */
    private syncButtonIcon(button: FabricImage, side: PagingSide): void {
        const urls = this.deps.icons[side];
        const state = !this.sideEnabled(side) ? 'disabled' : this.hovered === side ? 'hover' : 'enabled';
        const element = this.iconElements.get(urls[state]);
        if (element && button.getElement() !== element) {
            button.setElement(element);
            button.set({ dirty: true });
        }
    }

    private hitTestSide(point: { x: number; y: number }): PagingSide | null {
        for (const side of ['prev', 'next'] as const) {
            const pos = side === 'prev' ? PAGING_PREV_POSITION : PAGING_NEXT_POSITION;
            if (
                point.x >= pos.x &&
                point.x <= pos.x + PAGING_BUTTON_SIZE &&
                point.y >= pos.y &&
                point.y <= pos.y + PAGING_BUTTON_SIZE
            ) {
                return side;
            }
        }
        return null;
    }

    private setHover(side: PagingSide | null): void {
        if (side === this.hovered) return;
        this.hovered = side;
        // Restore the canvas's own default cursor (not the literal 'default') — don't fight the
        // host's grab-to-pan cursor, which fabric re-applies before each mouse:move.
        this.ctx.canvas.setCursor(side ? 'pointer' : this.ctx.canvas.defaultCursor);
        this.ctx.canvas.requestRenderAll(); // repaint picks up the icon swap via syncButtonIcon
    }

    private async preloadIcons(): Promise<void> {
        const urls = new Set<string>();
        for (const side of ['prev', 'next'] as const) {
            const stateUrls = this.deps.icons[side];
            urls.add(stateUrls.enabled);
            urls.add(stateUrls.hover);
            urls.add(stateUrls.disabled);
        }
        await Promise.all(
            [...urls].map(async url => {
                try {
                    this.iconElements.set(url, await util.loadImage(url));
                } catch {
                    // Icon fetch failed: this URL stays absent from the map. A missing *enabled* icon
                    // means ensureButton() never creates that side's button, so it neither renders nor
                    // claims the pointer (see claims()). A missing hover/disabled variant just leaves
                    // syncButtonIcon on the current element (it only swaps when the target exists).
                }
            }),
        );
        if (!this.disposed) this.ctx.canvas.requestRenderAll();
    }
}
